import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";

import Button from "@/components/button";
import Input from "@/components/input";
import OtpInput from "@/components/otp-input";
import PasswordInput from "@/components/password-input";
import {
  usePasswordRecoveryInitMutation,
  usePasswordRecoveryCompleteMutation,
} from "@/queries/hooks/authHooks";

export const Route = createFileRoute("/auth/_layout/forgot-password")({
  component: ForgotPasswordPage,
});

type Step = "request" | "reset" | "success";

function validatePassword(pw: string, t: (k: string) => string): string | null {
  if (pw.length < 8) return t("passwordReset.passwordTooShort");
  if (!/[A-Z]/.test(pw)) return t("passwordReset.passwordNeedsUppercase");
  if (!/[a-z]/.test(pw)) return t("passwordReset.passwordNeedsLowercase");
  if (!/[0-9]/.test(pw)) return t("passwordReset.passwordNeedsNumber");
  if (!/[@$!%*?&]/.test(pw)) return t("passwordReset.passwordNeedsSpecial");
  return null;
}

function ForgotPasswordPage() {
  const { t } = useTranslation("auth");
  const [step, setStep] = useState<Step>("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const initMutation = usePasswordRecoveryInitMutation();
  const completeMutation = usePasswordRecoveryCompleteMutation();

  const sendCode = async () => {
    if (!email.trim()) {
      setErrorMsg(t("forgotSms.enterEmailError"));
      return;
    }
    setErrorMsg(null);
    try {
      await initMutation.mutateAsync({ email: email.trim() });
      setStep("reset");
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : t("forgotSms.genericError"));
    }
  };

  const submitReset = async () => {
    setErrorMsg(null);
    if (code.length !== 6) {
      setErrorMsg(t("forgotSms.enterCodeError"));
      return;
    }
    const validationError = validatePassword(newPassword, t);
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg(t("passwordReset.passwordsDoNotMatch"));
      return;
    }
    try {
      await completeMutation.mutateAsync({
        email: email.trim(),
        code,
        newPassword,
      });
      setStep("success");
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : t("passwordReset.resetError"));
    }
  };

  if (step === "success") {
    return (
      <>
        <h1 className="heading-1 mb-5">{t("passwordReset.successTitle")}</h1>
        <img src="/checkmark-2.svg" alt="Success" width={60} height={60} className="mb-6" />
        <p className="body mb-6">{t("passwordReset.successBody")}</p>
        <Button variant="secondary" className="mt-8 w-[320px]" asChild>
          <Link to="/auth/login">{t("passwordReset.logIn")}</Link>
        </Button>
      </>
    );
  }

  if (step === "reset") {
    return (
      <>
        <h1 className="heading-1 mb-5">{t("forgotSms.resetTitle")}</h1>
        <img src="/email-2fa.svg" alt="SMS verification" width={60} height={60} className="mb-6" />
        <p className="body mb-1">
          <Trans
            i18nKey="forgotSms.codeSent"
            ns="auth"
            values={{ email }}
            components={{ strong: <strong /> }}
          />
        </p>
        <p className="body mb-4">{t("forgotSms.enterCodePrompt")}</p>
        <OtpInput maxLength={6} onComplete={(c) => setCode(c)} />
        <div className="flex flex-col gap-4 w-full max-w-[320px] mt-6">
          <PasswordInput
            label={t("passwordReset.newPasswordLabel")}
            placeholder={t("passwordReset.passwordPlaceholder")}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <PasswordInput
            label={t("passwordReset.repeatPasswordLabel")}
            placeholder={t("passwordReset.passwordPlaceholder")}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitReset();
            }}
          />
        </div>
        {errorMsg && <p className="body-small text-red-500 mt-2">{errorMsg}</p>}
        <Button
          variant="secondary"
          className="mt-8 w-[320px]"
          onClick={submitReset}
          disabled={completeMutation.isPending}
        >
          {completeMutation.isPending
            ? t("passwordReset.submitting")
            : t("passwordReset.submit")}
        </Button>
        <Button
          variant="link"
          className="mt-4"
          onClick={sendCode}
          disabled={initMutation.isPending}
        >
          {t("forgotSms.resendCode")}
        </Button>
      </>
    );
  }

  return (
    <>
      <h1 className="heading-1 mb-5">{t("forgotSms.title")}</h1>
      <img src="/email-2fa.svg" alt="SMS verification" width={60} height={60} className="mb-6" />
      <p className="body mb-6">{t("forgotSms.description")}</p>
      <Input
        label={t("forgot.emailLabel")}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") sendCode();
        }}
      />
      {errorMsg && <p className="body-small text-red-500 mt-2">{errorMsg}</p>}
      <div className="border-[#200A0A] w-full border mt-3" />
      <Button
        variant="secondary"
        className="mt-16 w-[320px]"
        onClick={sendCode}
        disabled={initMutation.isPending}
      >
        {initMutation.isPending ? t("forgotSms.sending") : t("forgotSms.submit")}
      </Button>
      <Button variant="link" className="mt-4" asChild>
        <Link to="/auth/login">{t("forgot.backToLogin")}</Link>
      </Button>
    </>
  );
}
